import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    FileCheck2,
    Compass,
    HardHat,
    Clock,
    Globe2,
    Users,
    Star,
    ShieldCheck,
    Quote,
} from "lucide-react";
import LeadForm from "../components/LeadForm";
import Estimator from "../components/Estimator";

const HERO_BG =
    "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50c3xlbnwwfHx8fDE3NzcwNTQ2NzV8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
    {
        icon: FileCheck2,
        title: "Permit Processing",
        desc: "End-to-end permit submittals, corrections, and approvals—handled by experts.",
    },
    {
        icon: Compass,
        title: "Architectural Plans",
        desc: "Code-compliant plans for residential, ADUs, and multifamily projects.",
    },
    {
        icon: HardHat,
        title: "Engineering Coordination",
        desc: "Structural, civil, and MEP coordination with licensed professionals.",
    },
];

const TESTIMONIALS = [
    {
        name: "Jessica Moreno",
        role: "Developer, Vista Homes",
        quote:
            "EZ Permit Express turned a 6-month bottleneck into a 6-week approval. Their team knows every jurisdiction inside out.",
        img: "https://images.pexels.com/photos/7937962/pexels-photo-7937962.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
        name: "Marcus Thompson",
        role: "General Contractor",
        quote:
            "I stopped chasing city offices. They handle the entire expediting process and keep me updated daily. Worth every penny.",
        img: "https://images.pexels.com/photos/36453960/pexels-photo-36453960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
        name: "Priya Shah",
        role: "Homeowner — ADU Project",
        quote:
            "Stress-free from plans to final sign-off. They answered every question and made the whole process feel effortless.",
        img: "https://images.pexels.com/photos/8117466/pexels-photo-8117466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
];

export default function Home() {
    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/95 via-[#0A2540]/80 to-[#0A2540]/40" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-28 md:py-36">
                    <div className="max-w-3xl fade-up">
                        <span className="inline-flex items-center gap-2 uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">
                            <Globe2 size={14} /> Nationwide Service
                        </span>
                        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
                            We Handle Your Plans &amp; Permits{" "}
                            <span className="text-[#C5A059]">From Start to Approval</span>
                        </h1>
                        <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">
                            Nationwide service. Fast, reliable, and stress-free permitting for homeowners, developers, investors, and contractors.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                className="btn-gold px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2"
                                data-testid="hero-cta-start"
                            >
                                Start Your Project <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/contact"
                                className="px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-[#0A2540] transition-colors"
                                data-testid="hero-cta-quote"
                            >
                                Get a Quote
                            </Link>
                        </div>

                        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
                            {[
                                { k: "50", s: "States Served" },
                                { k: "2,400+", s: "Permits Approved" },
                                { k: "40%", s: "Faster Turnaround" },
                                { k: "4.9/5", s: "Client Rating" },
                            ].map((s) => (
                                <div key={s.s}>
                                    <div className="text-3xl md:text-4xl font-bold text-white font-[Outfit]">{s.k}</div>
                                    <div className="mt-1 text-xs uppercase tracking-wider text-white/60">{s.s}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 3-STEP PROCESS */}
            <section className="py-24 bg-[#F8FAFC]" data-testid="process-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">Our Process</div>
                        <h2 className="mt-3 text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                            Permits approved in three simple steps
                        </h2>
                        <p className="mt-4 text-[#475569]">
                            We built a predictable workflow so you always know what's next—without the back-and-forth with city offices.
                        </p>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { n: "01", t: "Submit your project", d: "Share your plans or project brief. We review your scope and jurisdiction requirements." },
                            { n: "02", t: "We prepare plans & handle permits", d: "Our team drafts code-compliant plans, coordinates engineering, and submits to the city." },
                            { n: "03", t: "You get approved and build", d: "We manage corrections and expediting until you receive stamped, approved permits." },
                        ].map((s, i) => (
                            <div
                                key={s.n}
                                className="relative bg-white border border-[#E2E8F0] p-8 hover:shadow-md transition-shadow"
                                data-testid={`process-step-${i + 1}`}
                            >
                                <div className="text-5xl font-bold text-[#C5A059] font-[Outfit]">{s.n}</div>
                                <h3 className="mt-5 text-xl font-semibold text-[#0A2540]">{s.t}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#475569]">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES PREVIEW */}
            <section className="py-24 bg-white" data-testid="services-preview">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <div>
                            <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">What We Do</div>
                            <h2 className="mt-3 text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                                Everything you need for approval
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            className="text-sm font-semibold text-[#0A2540] hover:text-[#C5A059] inline-flex items-center gap-2 transition-colors"
                            data-testid="services-preview-link"
                        >
                            View all services <ArrowRight size={14} />
                        </Link>
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {SERVICES.map((s) => (
                            <div
                                key={s.title}
                                className="bg-white border border-[#E2E8F0] p-8 hover:border-[#C5A059] transition-colors group"
                            >
                                <div className="w-12 h-12 flex items-center justify-center bg-[#0A2540] text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-white transition-colors">
                                    <s.icon size={22} />
                                </div>
                                <h3 className="mt-6 text-xl font-semibold text-[#0A2540]">{s.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#475569]">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="py-24 bg-[#0A2540] text-white relative overflow-hidden" data-testid="benefits-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {[
                            { icon: Clock, title: "Fast Turnaround", desc: "Up to 40% faster approvals through our expediting network and direct jurisdiction relationships." },
                            { icon: Globe2, title: "Nationwide Coverage", desc: "Permits processed in all 50 states. We know local codes, inspectors, and submittal requirements." },
                            { icon: Users, title: "Expert Team", desc: "Licensed architects, structural engineers, and permit specialists working on your project." },
                        ].map((b) => (
                            <div key={b.title} className="flex flex-col">
                                <div className="w-14 h-14 flex items-center justify-center bg-[#C5A059]">
                                    <b.icon size={24} className="text-white" />
                                </div>
                                <h3 className="mt-6 text-2xl font-semibold">{b.title}</h3>
                                <p className="mt-3 text-white/70 leading-relaxed">{b.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ESTIMATOR */}
            <Estimator />

            {/* TESTIMONIALS */}
            <section className="py-24 bg-[#F8FAFC]" data-testid="testimonials-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">Client Stories</div>
                        <h2 className="mt-3 text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                            Trusted by builders, developers, and homeowners
                        </h2>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {TESTIMONIALS.map((t) => (
                            <figure
                                key={t.name}
                                className="bg-white border border-[#E2E8F0] p-8 flex flex-col"
                                data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
                            >
                                <Quote size={24} className="text-[#C5A059]" />
                                <blockquote className="mt-4 text-[#0A2540] leading-relaxed">"{t.quote}"</blockquote>
                                <div className="mt-6 flex items-center gap-4">
                                    <img
                                        src={t.img}
                                        alt={t.name}
                                        className="w-12 h-12 object-cover rounded-full"
                                    />
                                    <div>
                                        <figcaption className="font-semibold text-[#0A2540]">{t.name}</figcaption>
                                        <div className="text-xs text-[#475569]">{t.role}</div>
                                    </div>
                                </div>
                                <div className="mt-4 flex gap-1 text-[#C5A059]">
                                    {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                                </div>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACT / LEAD FORM */}
            <section className="py-24 bg-white" data-testid="home-contact-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
                    <div>
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">Get Started</div>
                        <h2 className="mt-3 text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                            Tell us about your project
                        </h2>
                        <p className="mt-4 text-[#475569] leading-relaxed">
                            Submit your details and our permit specialists will reach out within one business day with a tailored quote and timeline.
                        </p>

                        <div className="mt-10 space-y-5">
                            {[
                                { icon: ShieldCheck, t: "No obligation quote", d: "Free project review and fee estimate." },
                                { icon: Clock, t: "1 business day response", d: "We move fast so your project does too." },
                                { icon: Globe2, t: "Works everywhere", d: "Any jurisdiction, any permit complexity." },
                            ].map((f) => (
                                <div key={f.t} className="flex gap-4">
                                    <div className="w-10 h-10 flex items-center justify-center bg-[#0A2540] text-[#C5A059] flex-shrink-0">
                                        <f.icon size={18} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-[#0A2540]">{f.t}</div>
                                        <div className="text-sm text-[#475569]">{f.d}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-8 md:p-10">
                        <LeadForm source="home" />
                    </div>
                </div>
            </section>
        </div>
    );
}
